import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import { FaFileInvoiceDollar, FaHome, FaMapMarkedAlt } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

const StampDutyCalculator: React.FC = () => {
  const [inputs, setInputs] = useState({
    state: "NSW",
    propertyValue: 600000,
    isFirstHome: "yes",
    propertyType: "residential",
  });

  const [results, setResults] = useState<any>(null);

  const handleInputChange = (field: string, value: any) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const calculateStampDuty = (state: string, value: number, isFirstHome: boolean) => {
    let duty = 0;

    // Simplified stamp duty calculations for each state
    switch (state) {
      case "NSW":
        if (isFirstHome && value <= 800000) {
          duty = 0; // First home buyer exemption
        } else {
          duty = value * 0.04; // Simplified 4%
        }
        break;
      case "VIC":
        if (isFirstHome && value <= 600000) {
          duty = 0;
        } else {
          duty = value * 0.055; // Simplified 5.5%
        }
        break;
      case "QLD":
        duty = value * 0.0375; // Simplified 3.75%
        break;
      case "WA":
        duty = value * 0.04; // Simplified 4%
        break;
      case "SA":
        duty = value * 0.045; // Simplified 4.5%
        break;
      case "TAS":
        duty = value * 0.04; // Simplified 4%
        break;
      case "NT":
        duty = value * 0.055; // Simplified 5.5%
        break;
      case "ACT":
        duty = 0; // ACT abolished stamp duty
        break;
    }

    return duty;
  };

  const calculate = () => {
    const isFirstHome = inputs.isFirstHome === "yes";
    const stampDuty = calculateStampDuty(inputs.state, inputs.propertyValue, isFirstHome);
    const transferFee = 500; // Simplified transfer fee
    const totalUpfrontCosts = stampDuty + transferFee;

    setResults({
      stampDuty: stampDuty.toFixed(0),
      transferFee: transferFee.toFixed(0),
      totalCosts: totalUpfrontCosts.toFixed(0),
    });
  };

  const reset = () => {
    setInputs({
      state: "NSW",
      propertyValue: 600000,
      isFirstHome: "yes",
      propertyType: "residential",
    });
    setResults(null);
  };

  const stateComparison = [
    { state: "NSW", rate: "~4.0%", firstHomeBuyer: "Up to $800k" },
    { state: "VIC", rate: "~5.5%", firstHomeBuyer: "Up to $600k" },
    { state: "QLD", rate: "~3.75%", firstHomeBuyer: "Concessions available" },
    { state: "WA", rate: "~4.0%", firstHomeBuyer: "Varies" },
    { state: "SA", rate: "~4.5%", firstHomeBuyer: "Concessions available" },
    { state: "TAS", rate: "~4.0%", firstHomeBuyer: "Varies" },
    { state: "NT", rate: "~5.5%", firstHomeBuyer: "Varies" },
    { state: "ACT", rate: "Abolished", firstHomeBuyer: "N/A" },
  ];

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/stamp-duty"
        fallbackTitle="Stamp Duty Calculator"
        fallbackDescription="Calculate stamp duty costs for property purchases across Australian states"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bgGradient="linear(to-r, red.500, orange.400)" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaFileInvoiceDollar} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Stamp Duty Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              Calculate state-specific stamp duty for your property purchase
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Single Column Layout */}
      <Container maxW="container.md" py={12}>
        <VStack spacing={8} align="stretch">
          {/* State Selector at Top */}
          <Card borderTop="4px solid" borderColor="orange.500">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Icon as={FaMapMarkedAlt} color="orange.500" boxSize={6} />
                  <Heading size="md" color="orange.600">
                    Select State/Territory
                  </Heading>
                </HStack>
                <Divider />

                <FormControl>
                  <FormLabel fontSize="lg">State/Territory</FormLabel>
                  <Select
                    size="lg"
                    value={inputs.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                  >
                    <option value="NSW">New South Wales (NSW)</option>
                    <option value="VIC">Victoria (VIC)</option>
                    <option value="QLD">Queensland (QLD)</option>
                    <option value="WA">Western Australia (WA)</option>
                    <option value="SA">South Australia (SA)</option>
                    <option value="TAS">Tasmania (TAS)</option>
                    <option value="NT">Northern Territory (NT)</option>
                    <option value="ACT">Australian Capital Territory (ACT)</option>
                  </Select>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Property Details */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Icon as={FaHome} color="blue.500" boxSize={6} />
                  <Heading size="md" color="blue.600">
                    Property Details
                  </Heading>
                </HStack>
                <Divider />

                <FormControl>
                  <FormLabel>Property Value</FormLabel>
                  <Input
                    type="number"
                    size="lg"
                    value={inputs.propertyValue}
                    onChange={(e) =>
                      handleInputChange("propertyValue", parseFloat(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Purchase price or market value
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>First Home Buyer?</FormLabel>
                  <RadioGroup
                    value={inputs.isFirstHome}
                    onChange={(value) => handleInputChange("isFirstHome", value)}
                  >
                    <Stack direction="row" spacing={6}>
                      <Radio value="yes" size="lg">
                        Yes
                      </Radio>
                      <Radio value="no" size="lg">
                        No
                      </Radio>
                    </Stack>
                  </RadioGroup>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    First home buyers may be eligible for exemptions or concessions
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Property Type</FormLabel>
                  <Select
                    value={inputs.propertyType}
                    onChange={(e) => handleInputChange("propertyType", e.target.value)}
                  >
                    <option value="residential">Residential</option>
                    <option value="investment">Investment</option>
                    <option value="commercial">Commercial</option>
                  </Select>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Calculate Button */}
          <HStack spacing={4}>
            <Button colorScheme="orange" size="lg" onClick={calculate} flex={1} height="60px">
              Calculate Stamp Duty
            </Button>
            <Button variant="outline" size="lg" onClick={reset} height="60px">
              Reset
            </Button>
          </HStack>

          {/* Results */}
          {results && (
            <Card bg="orange.50" borderWidth={2} borderColor="orange.400">
              <CardBody>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <Heading size="lg" color="orange.700">
                      {inputs.state} Stamp Duty
                    </Heading>
                    <Badge colorScheme="orange" fontSize="md" px={4} py={2}>
                      Estimated
                    </Badge>
                  </HStack>

                  <Divider />

                  <Box textAlign="center" py={4}>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Stamp Duty
                    </Text>
                    <Heading size="3xl" color="orange.600">
                      ${parseInt(results.stampDuty).toLocaleString()}
                    </Heading>
                  </Box>

                  <Divider />

                  <VStack spacing={4} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">
                        Property Value:
                      </Text>
                      <Text fontWeight="bold" fontSize="lg">
                        ${inputs.propertyValue.toLocaleString()}
                      </Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">
                        Stamp Duty:
                      </Text>
                      <Text fontWeight="bold" fontSize="lg" color="orange.600">
                        ${parseInt(results.stampDuty).toLocaleString()}
                      </Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">
                        Transfer Fees:
                      </Text>
                      <Text fontWeight="bold" fontSize="lg">
                        ${parseInt(results.transferFee).toLocaleString()}
                      </Text>
                    </HStack>

                    <Divider />

                    <Box bg="orange.100" p={4} borderRadius="md">
                      <HStack justify="space-between">
                        <Text fontSize="lg" fontWeight="bold" color="orange.800">
                          Total Upfront Costs:
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="orange.700">
                          ${parseInt(results.totalCosts).toLocaleString()}
                        </Text>
                      </HStack>
                    </Box>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* State Comparison Table */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" color="gray.700">
                  State-by-State Comparison
                </Heading>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>State</Th>
                      <Th>Approx. Rate</Th>
                      <Th>First Home Buyer</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stateComparison.map((item) => (
                      <Tr
                        key={item.state}
                        bg={inputs.state === item.state ? "orange.50" : "transparent"}
                        fontWeight={inputs.state === item.state ? "bold" : "normal"}
                      >
                        <Td>{item.state}</Td>
                        <Td>{item.rate}</Td>
                        <Td fontSize="xs">{item.firstHomeBuyer}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Text fontSize="xs" color="gray.500">
                  Note: Rates are simplified averages. Actual stamp duty uses progressive brackets
                  and varies based on many factors. Always verify with state revenue office.
                </Text>

                {results && (
                  <LeadCaptureForm
                    calculatorType={CalculatorType.StampDuty}
                    calculatorName="Stamp Duty Calculator"
                    calculatorPath="/calculators/stamp-duty"
                    inputData={inputs}
                    resultData={results}
                    hasResults={!!results}
                    accentColor="orange"
                  />
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default StampDutyCalculator;
